import { useDispatch, useSelector } from 'react-redux';
import { updateDevProjectBody } from '../../features/projects/devprojectSlice';
import { 
  Button, 
  Typography, 
  Card,
  message,
  Spin,
  Space,
  Tag
} from 'antd';
import {
  CodeOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import Editor from '../other/Editor';

const { Title, Text } = Typography;

const EditDevForm = ({ 
  selectedProject, 
  onCancel, 
  onSave, 
  bodyContent, 
  setBodyContent 
}) => {
  const dispatch = useDispatch();
  const { bodyLoading, bodyError } = useSelector((state) => state.devprojects);

  // Helper function to check if content is truly empty
  const isContentEmpty = (content) => {
    if (!content) return true;
    
    // Remove HTML tags and check if any meaningful content remains
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim(); // Remove whitespace
    
    return textContent === '';
  };

  const handleBodyChange = (content) => {
    setBodyContent(content);
  };

  const handleSaveContent = async () => {
    if (!selectedProject) {
      message.error('No project selected');
      return;
    }

    // Check if content is truly empty (not just HTML tags)
    const isEmpty = isContentEmpty(bodyContent);
    
    // If content is empty, save as empty string to clear the field
    const contentToSave = isEmpty ? '' : bodyContent;

    try {
      await dispatch(updateDevProjectBody({ 
        id: selectedProject._id, 
        body: contentToSave
      })).unwrap();
      
      message.success(isEmpty ? 'Content cleared successfully!' : 'Content saved successfully!');
      
      // Call the onSave callback if provided
      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('Save error:', err);
      message.error('Failed to save content: ' + (err.message || 'Unknown error'));
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getPriorityDisplay = (priority) => {
    switch (priority) {
      case 'featured':
        return <Tag color="gold" icon={<StarFilled />}>Featured Project</Tag>;
      case 'other':
        return <Tag color="blue" icon={<StarOutlined />}>Other Project</Tag>;
      default:
        return <Tag color="default">No Priority Set</Tag>;
    }
  };

  if (!selectedProject) {
    return (
      <Card className="mb-6">
        <div className="text-center py-12">
          <CodeOutlined className="text-6xl text-gray-400 mb-4" />
          <Title level={4} className="text-gray-500 mb-2">
            No Project Selected
          </Title>
          <Text className="text-gray-400 mb-6 block">
            Please select a development project to edit its content
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      {/* Selected Project Info */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            {selectedProject.url ? (
              <img
                src={selectedProject.url}
                alt={selectedProject.name}
                className="w-16 h-16 object-cover rounded-lg"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                <CodeOutlined className="text-blue-600" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <CodeOutlined className="text-blue-600" />
              <Title level={4} className="m-0">
                {selectedProject.name}
              </Title>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                {selectedProject.type}
              </span>
              {getPriorityDisplay(selectedProject.priority)}
            </div>
            <Text className="text-gray-600 block mb-2">
              {selectedProject.desc}
            </Text>
            <Text className="text-blue-600 text-sm">
              Add detailed technical documentation, features, and development process
            </Text>
          </div>
        </div>
      </Card>

      {/* Editor Section */}
      <Card>
        <div className="mb-4">
          <Title level={5} className="mb-2">Content Editor</Title>
          <Text className="text-gray-600 text-sm">
            Use the rich text editor to add detailed project information, technical challenges, solutions, and development process.
          </Text>
        </div>
        
        {bodyLoading ? (
          <div className="text-center py-8">
            <Spin size="large" />
            <Text className="block mt-4 text-gray-500">Loading content...</Text>
          </div>
        ) : (
          <>
            <div className="editor-container border border-gray-200 rounded-lg" style={{ height: '700px', display: 'flex', flexDirection: 'column' }}>
              <Editor
                value={bodyContent}
                onChange={handleBodyChange}
                placeholder="Write detailed information about your project, technical challenges, solutions, features, development process, code snippets, architecture decisions..."
                minHeight="400px"
                stickyToolbar={true}
                style={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                editorStyle={{
                  height: 'calc(100% - 60px)', // Subtract toolbar height
                  overflow: 'auto',
                  border: 'none'
                }}
                toolbarStyle={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e8e8e8',
                  borderRadius: '8px 8px 0 0'
                }}
                onClear={() => setBodyContent('')}
              />
            </div>

            {/* Media Rendering Guide Section */}
            <Card className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="mb-3">
                <Title level={5} className="mb-2 text-blue-900 flex items-center">
                  <CodeOutlined className="mr-2" />
                  Media Rendering Guide
                </Title>
                <Text className="text-blue-700 text-sm">
                  Learn how to add different types of media displays to your content
                </Text>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Regular Media */}
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <Text strong className="text-green-700">Regular Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Single images/videos display as centered blocks
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Just paste the URL directly in text:<br/>
                    <span className="text-blue-600">https://example.com/image.jpg</span>
                  </div>
                </div>

                {/* Carousel Media */}
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <Text strong className="text-purple-700">Carousel Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple media in horizontal scrollable layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Create a list with media URLs:<br/>
                    • <span className="text-blue-600">https://image1.jpg</span><br/>
                    • <span className="text-blue-600">https://image2.jpg</span><br/>
                    • <span className="text-blue-600">https://video.mp4</span>
                  </div>
                </div>

                {/* Grid Media */}
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                    <Text strong className="text-orange-700">Grid Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple media in organized grid layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Start with layout config line:<br/>
                    • <span className="text-orange-600">layout=[grid] col=[3]</span><br/>
                    • <span className="text-blue-600">https://image1.jpg</span><br/>
                    • <span className="text-blue-600">https://image2.jpg</span><br/>
                    • <span className="text-blue-600">https://image3.jpg</span><br/>
                    • <span className="text-blue-600">https://image4.jpg</span>
                  </div>
                  
                </div>
              </div>

              {/* Supported Formats */}
              <div className="mt-4 p-3 bg-white rounded-lg border border-blue-100">
                <Text strong className="text-blue-900 block mb-2">Supported Formats:</Text>
                <div className="flex flex-wrap gap-2">
                  <Tag color="green">Images: .jpg, .png, .gif, .webp, .svg</Tag>
                  <Tag color="blue">Videos: .mp4, .webm, .ogg, .mov</Tag>
                  <Tag color="red">YouTube: youtube.com or youtu.be links</Tag>
                </div>
              </div>

              {/* Pro Tips */}
              <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <Text strong className="text-yellow-800 block mb-2">💡 Pro Tips:</Text>
                <ul className="text-sm text-yellow-700 space-y-1 ml-4">
                  <li>• Use lists for creating image galleries that scroll horizontally</li>
                  <li>• Add descriptive text before media URLs in lists for captions</li>
                  <li>• Lists with 4+ media items automatically get navigation arrows</li>
                  <li>• For grids: Start with "layout=[grid] col=[3]" then add media list</li>
                  <li>• Grid config: layout=[grid] col=[number of columns]</li>
                  <li>• Grid layouts automatically stack to single column on mobile</li>
                  <li>• YouTube links automatically convert to embedded players</li>
                  <li>• Mix different media types in the same list/grid for variety</li>
                </ul>
              </div>
            </Card>

            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
              <Text className="text-gray-500 text-sm">
                {bodyContent.length > 0 ? `${bodyContent.length} characters` : 'Start writing...'}
              </Text>
              <Space>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  onClick={handleSaveContent}
                  loading={bodyLoading}
                >
                  {isContentEmpty(bodyContent) ? 'Clear Content' : 'Save Content'}
                </Button>
              </Space>
            </div>
          </>
        )}

        {bodyError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <Text className="text-red-600">Error: {bodyError}</Text>
          </div>
        )}
      </Card>
    </>
  );
};

export default EditDevForm;